/**
 * Company Service
 * 
 * API service for company management.
 */

import { apiClient } from './api.service';
import type {
  ApproveCompanyDto,
  AssignAdvisorDto,
  Company,
  CompanyAdvisor,
  CompanyMedia,
  CompanyQueryParams,
  CompanyReferralAttribution,
  FinanceDashboardData,
  FinancePeriodKey,
  FinancePaymentsQuery,
  FinanceReconciliation,
  MyAdvisedCompaniesParams,
  PaginatedCompanies,
  PaginatedCompanyAuditLog,
  PaginatedFinancePayments,
  PaginatedFinancePayouts,
  PaginatedFinanceSummary,
  RejectCompanyDto,
  SuspendCompanyDto,
  UpdateAdvisorDto,
  UpdateCompanyDto,
} from '@/types/company.types';
import type {
  PaginatedStores,
  StoreQueryParams,
  StoreTeam,
} from '@/types/store.types';

// ============================================================================
// SERVICE
// ============================================================================

export const companyService = {
  /**
   * Aliado declarado al registrarse y aliado aprobado, juntos. Alimenta el
   * pre-llenado de la aprobación y la ficha de la compañía — hasta ahora la
   * atribución se escribía y no se leía en ninguna vista posterior.
   */
  async getReferralAttribution(
    companyId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<CompanyReferralAttribution> {
    const response = await apiClient.get<CompanyReferralAttribution>(
      `/companies/${companyId}/referral`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Get all companies (paginated) - requires countryId
   */
  async getAll(
    params: CompanyQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedCompanies> {
    const searchParams = new URLSearchParams();

    // countryId is required
    searchParams.append('countryId', params.countryId);

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.isVerified !== undefined) searchParams.append('isVerified', params.isVerified.toString());
    if (params.search) searchParams.append('search', params.search);

    const response = await apiClient.get<PaginatedCompanies>(
      `/companies?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Get company by ID
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Company> {
    const response = await apiClient.get<Company>(`/companies/${id}`, {
      signal: opts.signal,
    });
    return response.data;
  },

  /**
   * Update company
   */
  async update(id: string, data: UpdateCompanyDto): Promise<Company> {
    const response = await apiClient.patch<Company>(`/companies/${id}`, data);
    return response.data;
  },

  /**
   * Approve company
   */
  async approve(id: string, data: ApproveCompanyDto): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${id}/approve`, data);
    return response.data;
  },

  /**
   * Reject company
   */
  async reject(id: string, data: RejectCompanyDto): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${id}/reject`, data);
    return response.data;
  },

  /**
   * Suspend company
   */
  async suspend(id: string, data: SuspendCompanyDto): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${id}/suspend`, data);
    return response.data;
  },

  /**
   * Reactivate company
   */
  async reactivate(id: string): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${id}/reactivate`, {});
    return response.data;
  },

  /**
   * Delete company (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/companies/${id}`);
  },

  /**
   * Historial de auditoría (status changes, creates, etc.)
   */
  async getAuditLog(
    id: string,
    params: { skip?: number; take?: number } = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedCompanyAuditLog> {
    const q = new URLSearchParams();
    if (params.skip !== undefined) q.set('skip', String(params.skip));
    if (params.take !== undefined) q.set('take', String(params.take));
    const qs = q.toString();
    const res = await apiClient.get<PaginatedCompanyAuditLog>(
      `/companies/${id}/audit-log${qs ? `?${qs}` : ''}`,
      { signal: opts.signal },
    );
    return res.data;
  },

  /**
   * Get company documents (legal documents like RUT, etc.)
   */
  async getDocuments(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<CompanyMedia[]> {
    const response = await apiClient.get<CompanyMedia[]>(
      `/companies/${id}/documents`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Add document to company
   */
  async addDocument(id: string, mediaId: string): Promise<CompanyMedia> {
    const response = await apiClient.post<CompanyMedia>(`/companies/${id}/documents`, { mediaId });
    return response.data;
  },

  /**
   * Remove document from company
   */
  async removeDocument(id: string, mediaId: string): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(`/companies/${id}/documents/${mediaId}/remove`);
    return response.data;
  },

  /**
   * Get all company media (logos, covers, documents)
   */
  async getAllMedia(id: string): Promise<CompanyMedia[]> {
    const response = await apiClient.get<CompanyMedia[]>(`/companies/${id}/media`);
    return response.data;
  },

  /**
   * Get stores for a company (paginated)
   */
  async getStores(
    companyId: string,
    params: StoreQueryParams = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedStores> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.cityId) searchParams.append('cityId', params.cityId);
    if (typeof params.isOpen === 'boolean') searchParams.append('isOpen', params.isOpen.toString());
    if (typeof params.acceptsPickup === 'boolean') searchParams.append('acceptsPickup', params.acceptsPickup.toString());
    if (typeof params.acceptsDelivery === 'boolean') searchParams.append('acceptsDelivery', params.acceptsDelivery.toString());
    if (params.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get<PaginatedStores>(
      `/companies/${companyId}/stores${query ? `?${query}` : ''}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  // =========================================================================
  // ADVISOR MANAGEMENT
  // =========================================================================

  /**
   * Get all active advisors for a company
   */
  async getAdvisors(
    companyId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<CompanyAdvisor[]> {
    const response = await apiClient.get<CompanyAdvisor[]>(
      `/companies/${companyId}/advisors`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Assign an advisor to a company
   */
  async assignAdvisor(companyId: string, data: AssignAdvisorDto): Promise<CompanyAdvisor> {
    const response = await apiClient.post<CompanyAdvisor>(`/companies/${companyId}/advisors`, data);
    return response.data;
  },

  /**
   * Update an advisor assignment
   */
  async updateAdvisor(companyId: string, advisorId: string, data: UpdateAdvisorDto): Promise<CompanyAdvisor> {
    const response = await apiClient.patch<CompanyAdvisor>(`/companies/${companyId}/advisors/${advisorId}`, data);
    return response.data;
  },

  /**
   * Unassign an advisor from a company
   */
  async unassignAdvisor(companyId: string, advisorId: string): Promise<CompanyAdvisor> {
    const response = await apiClient.delete<CompanyAdvisor>(`/companies/${companyId}/advisors/${advisorId}`);
    return response.data;
  },

  /**
   * Get companies assigned to the current user as advisor (My Companies)
   */
  async getMyAdvisedCompanies(
    params: MyAdvisedCompaniesParams = {},
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedCompanies> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get<PaginatedCompanies>(
      `/companies/my-advised${query ? `?${query}` : ''}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  // =========================================================================
  // FINANCE
  // =========================================================================

  /**
   * Get companies with financial summary (revenue, orders, commission, subscription)
   * Used by FINANCE role dashboard.
   */
  async getFinanceSummary(
    params: CompanyQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedFinanceSummary> {
    const searchParams = new URLSearchParams();
    searchParams.append('countryId', params.countryId);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);

    const response = await apiClient.get<PaginatedFinanceSummary>(
      `/companies/finance-summary?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * KPIs agregados del panel financiero.
   *
   * El periodo viaja como clave (`CURRENT_MONTH`), no como fechas: quien las
   * calcula es el servidor, con la zona horaria del país. Si las calculara el
   * navegador usaría la suya, que es el error que se corrigió en el corte de
   * mes.
   *
   * `refresh` sólo se manda cuando el usuario pulsa "Actualizar": salta la
   * caché de 60 s del backend.
   */
  async getFinanceDashboard(
    countryId: string,
    opts: {
      signal?: AbortSignal;
      period?: FinancePeriodKey;
      refresh?: boolean;
    } = {},
  ): Promise<FinanceDashboardData> {
    const params = new URLSearchParams({ countryId });
    if (opts.period) params.append('period', opts.period);
    if (opts.refresh) params.append('refresh', 'true');

    const response = await apiClient.get<FinanceDashboardData>(
      `/companies/finance-dashboard?${params.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Get paginated list of orders with payment details for finance.
   */
  async getFinancePayments(
    params: FinancePaymentsQuery,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedFinancePayments> {
    const searchParams = new URLSearchParams();
    searchParams.append('countryId', params.countryId);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.orderStatus) searchParams.append('orderStatus', params.orderStatus);
    if (params.paymentStatus) searchParams.append('paymentStatus', params.paymentStatus);
    if (params.serviceType) searchParams.append('serviceType', params.serviceType);
    if (params.paymentMethod) searchParams.append('paymentMethod', params.paymentMethod);
    if (params.from) searchParams.append('from', params.from);
    if (params.to) searchParams.append('to', params.to);
    if (params.search) searchParams.append('search', params.search);

    const response = await apiClient.get<PaginatedFinancePayments>(
      `/companies/finance-payments?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Liquidaciones a comercios.
   *
   * `includeBankDetails` trae el número de cuenta y el documento del titular
   * **completos**; por defecto llegan enmascarados. Sólo lo piden la
   * exportación para el banco y el botón de revelar una liquidación concreta,
   * y el backend deja constancia de quién lo hizo.
   */
  async getFinancePayouts(
    params: {
      countryId: string;
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
      includeBankDetails?: boolean;
    },
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedFinancePayouts> {
    const searchParams = new URLSearchParams();
    searchParams.append('countryId', params.countryId);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.includeBankDetails)
      searchParams.append('includeBankDetails', 'true');

    const response = await apiClient.get<PaginatedFinancePayouts>(
      `/companies/finance-payouts?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Cuadre de conciliación del período — dinero cobrado vs obligaciones
   * con tiendas vs margen plataforma. Para confrontar contra el reporte
   * de liquidación de Wompi.
   */
  async getFinanceReconciliation(
    params: { countryId: string; from: string; to: string },
    opts: { signal?: AbortSignal } = {},
  ): Promise<FinanceReconciliation> {
    const searchParams = new URLSearchParams({
      countryId: params.countryId,
      from: params.from,
      to: params.to,
    });
    const response = await apiClient.get<FinanceReconciliation>(
      `/companies/finance-reconciliation?${searchParams.toString()}`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Equipo de una sucursal — dueño de la compañía + manager de la
   * tienda. Usado por el detalle de sucursal en admin para mostrar
   * ambos roles en una sola vista.
   */
  async getStoreTeam(
    companyId: string,
    storeId: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<StoreTeam> {
    const response = await apiClient.get<StoreTeam>(
      `/companies/${companyId}/stores/${storeId}/team`,
      { signal: opts.signal },
    );
    return response.data;
  },
};

export default companyService;
